/**
 * Form Validation Utilities
 *
 * UX15: Common validation functions for immediate feedback.
 * All validators return error messages or empty string if valid.
 */

/**
 * Validate required field
 */
export function validateRequired(value: string | null | undefined, fieldName = 'This field'): string {
  if (!value || value.trim() === '') {
    return `${fieldName} is required`
  }
  return ''
}

/**
 * Validate email format
 */
export function validateEmail(email: string): string {
  if (!email) return ''

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address'
  }

  return ''
}

/**
 * Validate URL format
 */
export function validateURL(url: string): string {
  if (!url) return ''

  try {
    new URL(url)
    return ''
  } catch {
    return 'Please enter a valid URL (e.g., https://example.com)'
  }
}

/**
 * Validate LinkedIn URL
 */
export function validateLinkedInURL(url: string): string {
  if (!url) return ''

  const urlError = validateURL(url)
  if (urlError) return urlError

  if (!url.includes('linkedin.com')) {
    return 'Please enter a LinkedIn profile URL'
  }

  return ''
}

/**
 * Validate minimum length
 */
export function validateMinLength(value: string, minLength: number, fieldName = 'This field'): string {
  if (!value) return ''

  if (value.length < minLength) {
    return `${fieldName} must be at least ${minLength} characters`
  }

  return ''
}

/**
 * Validate maximum length
 */
export function validateMaxLength(value: string, maxLength: number, fieldName = 'This field'): string {
  if (!value) return ''

  if (value.length > maxLength) {
    return `${fieldName} must be at most ${maxLength} characters`
  }

  return ''
}

/**
 * Validate number range
 */
export function validateNumberRange(
  value: number | string,
  min: number,
  max: number,
  fieldName = 'This field'
): string {
  const num = typeof value === 'string' ? parseFloat(value) : value

  if (isNaN(num)) {
    return `${fieldName} must be a valid number`
  }

  if (num < min || num > max) {
    return `${fieldName} must be between ${min} and ${max}`
  }

  return ''
}

/**
 * Validate password strength
 */
export function validatePassword(password: string): string {
  if (!password) return ''

  if (password.length < 8) {
    return 'Password must be at least 8 characters'
  }

  if (!/[A-Z]/.test(password)) {
    return 'Password must contain at least one uppercase letter'
  }

  if (!/[a-z]/.test(password)) {
    return 'Password must contain at least one lowercase letter'
  }

  if (!/[0-9]/.test(password)) {
    return 'Password must contain at least one number'
  }

  return ''
}

/**
 * Validate password confirmation
 */
export function validatePasswordConfirmation(password: string, confirmation: string): string {
  if (!confirmation) return ''

  if (password !== confirmation) {
    return 'Passwords do not match'
  }

  return ''
}

/**
 * Validate phone number (US format)
 */
export function validatePhoneUS(phone: string): string {
  if (!phone) return ''

  const phoneRegex = /^(\+1|1)?[-.\s]?\(?[2-9]\d{2}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/
  if (!phoneRegex.test(phone.replace(/\s/g, ''))) {
    return 'Please enter a valid US phone number'
  }

  return ''
}

/**
 * Validate alphanumeric (letters and numbers only)
 */
export function validateAlphanumeric(value: string, fieldName = 'This field'): string {
  if (!value) return ''

  const alphanumericRegex = /^[a-zA-Z0-9]+$/
  if (!alphanumericRegex.test(value)) {
    return `${fieldName} can only contain letters and numbers`
  }

  return ''
}

/**
 * Compose multiple validators
 * Returns the first error found, or empty string if all pass
 *
 * @example
 * ```typescript
 * const emailValidator = composeValidators(
 *   (v) => validateRequired(v, 'Email'),
 *   (v) => validateEmail(v)
 * )
 * ```
 */
export function composeValidators(
  ...validators: Array<(value: any) => string>
): (value: any) => string {
  return (value: any) => {
    for (const validator of validators) {
      const error = validator(value)
      if (error) return error
    }
    return ''
  }
}

/**
 * Create a validation function for a form
 *
 * @example
 * ```typescript
 * const validateLoginForm = createFormValidator<{ email: string; password: string }>({
 *   email: composeValidators(
 *     (v) => validateRequired(v, 'Email'),
 *     (v) => validateEmail(v)
 *   ),
 *   password: (v) => validateRequired(v, 'Password'),
 * })
 * ```
 */
export function createFormValidator<T extends Record<string, any>>(
  fieldValidators: Partial<Record<keyof T, (value: any) => string>>
): (values: T) => Record<string, string> {
  return (values: T) => {
    const errors: Record<string, string> = {}

    for (const field in fieldValidators) {
      const validator = fieldValidators[field]
      if (validator) {
        const error = validator(values[field])
        if (error) {
          errors[field] = error
        }
      }
    }

    return errors
  }
}
